import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GuestPointsIncentive } from '@/components/points/GuestPointsIncentive';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/config/routes';
import {
  splitPaymentApi,
  type SessionBill,
  type SessionBillReceipt,
} from '@/lib/api/split-payment';
import { formatCurrency } from '@/utils/currency';
import { clearPendingBillReceipt } from '@/lib/billReceiptContext';

interface Props {
  sessionToken: string;
  deviceToken: string;
  bills: readonly SessionBill[];
  comensalId: number;
  /** 현장 결제 뒤 세션이 종료돼 bills를 잃어도 저장된 Bill 영수증을 복구한다. */
  autoOpenBillId?: number;
}

export function BillReceiptViewer({
  sessionToken,
  deviceToken,
  bills,
  comensalId,
  autoOpenBillId,
}: Props) {
  const { locale, currency } = useApp();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<SessionBillReceipt | null>(null);
  const [error, setError] = useState<'pending' | 'failed' | null>(null);
  const [loadingBillId, setLoadingBillId] = useState<number | null>(null);
  const inFlightRef = useRef(false);
  const autoPollCountRef = useRef(0);
  const mountedRef = useRef(true);
  const settledBills = bills.filter((bill) =>
    bill.comensal_id === comensalId && (bill.status === 'settled' || bill.status === 'refunded'));

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadReceipt = useCallback(async (billId: number): Promise<void> => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoadingBillId(billId);
    try {
      const nextReceipt = await splitPaymentApi.getBillReceipt(sessionToken, deviceToken, billId);
      if (!mountedRef.current) return;
      setReceipt(nextReceipt);
      setError(null);
      autoPollCountRef.current = 0;
      // 자동 복구 중인 결제만 소비한다. 과거 영수증을 수동으로 열었다고 해서
      // 별도로 처리 중인 Bill의 복구 단서를 지우면 안 된다.
      if (autoOpenBillId === billId) clearPendingBillReceipt();
    } catch (caught) {
      if (!mountedRef.current) return;
      const status = typeof caught === 'object' && caught !== null && 'status' in caught
        ? (caught as { status?: unknown }).status
        : null;
      setError(status === 409 && autoPollCountRef.current < 12 ? 'pending' : 'failed');
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoadingBillId(null);
    }
  }, [autoOpenBillId, deviceToken, sessionToken]);

  useEffect(() => {
    autoPollCountRef.current = 0;
  }, [autoOpenBillId]);

  useEffect(() => {
    if (autoOpenBillId === undefined || receipt?.bill.id === autoOpenBillId) return;
    void loadReceipt(autoOpenBillId);
  }, [autoOpenBillId, loadReceipt, receipt?.bill.id]);

  // BILL_NOT_SETTLED(409)만 자동으로 다시 확인한다. 권한/영구/서버 오류까지 계속
  // 두드리면 화면을 떠날 때까지 무한 요청이 되므로 그 경우에는 수동 갱신만 허용한다.
  useEffect(() => {
    if (
      autoOpenBillId === undefined
      || receipt?.bill.id === autoOpenBillId
      || error !== 'pending'
    ) return;

    const timer = window.setInterval(() => {
      if (inFlightRef.current) return;
      if (autoPollCountRef.current >= 12) {
        setError('failed');
        return;
      }
      autoPollCountRef.current += 1;
      void loadReceipt(autoOpenBillId);
    }, 5000);
    return () => { window.clearInterval(timer); };
  }, [autoOpenBillId, error, loadReceipt, receipt?.bill.id]);

  if (settledBills.length === 0 && autoOpenBillId === undefined) return null;

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4" data-testid="bill-receipt-viewer">
      <h3 className="text-sm font-semibold text-foreground">
        {locale === 'es' ? 'Mis recibos' : locale === 'ko' ? '내 영수증' : 'My receipts'}
      </h3>
      <div className="flex flex-wrap gap-2">
        {settledBills.map((bill) => (
          <button
            key={bill.id}
            type="button"
            onClick={() => { void loadReceipt(bill.id); }}
            disabled={loadingBillId !== null}
            className="rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50"
            data-testid={`bill-receipt-open-${String(bill.id)}`}
          >
            {locale === 'es' ? 'Ver recibo' : locale === 'ko' ? '영수증 보기' : 'View receipt'} #{bill.sequence_no}
          </button>
        ))}
      </div>

      {loadingBillId !== null && receipt === null ? (
        <p className="text-sm text-muted-foreground" role="status">
          {locale === 'es' ? 'Consultando el estado del pago…' : locale === 'ko' ? '결제 상태 확인 중…' : 'Checking payment status…'}
        </p>
      ) : null}

      {error !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {error === 'pending'
            ? locale === 'es'
              ? 'El pago sigue en proceso. Actualizaremos el recibo cuando quede confirmado.'
              : locale === 'ko'
                ? '결제를 처리하고 있습니다. 확정되면 영수증을 갱신합니다.'
                : 'The payment is still processing. We will refresh the receipt when it is confirmed.'
            : locale === 'es'
              ? 'No se pudo cargar el recibo.'
              : locale === 'ko'
                ? '영수증을 불러오지 못했습니다.'
                : 'Could not load the receipt.'}
        </p>
      ) : null}

      {autoOpenBillId !== undefined && receipt === null ? (
        <button
          type="button"
          onClick={() => {
            autoPollCountRef.current = 0;
            void loadReceipt(autoOpenBillId);
          }}
          disabled={loadingBillId !== null}
          className="rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50"
          data-testid="bill-receipt-refresh"
        >
          {locale === 'es' ? 'Actualizar estado' : locale === 'ko' ? '상태 새로고침' : 'Refresh status'}
        </button>
      ) : null}

      {receipt !== null ? (
        <div className="space-y-3 border-t border-border pt-3" data-testid="bill-receipt-details">
          <ul className="space-y-2">
            {receipt.items.map((item) => (
              <li
                key={`${String(item.order_id)}:${String(item.order_item_id)}`}
                className="flex items-start justify-between gap-3 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate">{item.name}</span>
                  <span
                    className="block text-xs text-muted-foreground"
                    data-testid={`bill-receipt-share-${String(item.order_item_id)}`}
                  >
                    {locale === 'es' ? 'Participación' : locale === 'ko' ? '지분' : 'Share'}:{' '}
                    {(item.share_ppm / 10000).toLocaleString(locale, {
                      maximumFractionDigits: 2,
                    })}%
                  </span>
                </span>
                <span className="tabular-nums">{formatCurrency(item.amount, currency)}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between border-t border-border pt-2 font-semibold">
            <span>{locale === 'es' ? 'Total' : locale === 'ko' ? '합계' : 'Total'}</span>
            <span className="tabular-nums">{formatCurrency(receipt.amounts.total, currency)}</span>
          </div>
          {receipt.amounts.tip > 0 ? (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{locale === 'es' ? 'Propina' : locale === 'ko' ? '팁' : 'Tip'}</span>
              <span className="tabular-nums" data-testid="bill-receipt-tip">
                {formatCurrency(receipt.amounts.tip, currency)}
              </span>
            </div>
          ) : null}
          {receipt.amounts.paid > 0 ? (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{locale === 'es' ? 'Pagado' : locale === 'ko' ? '결제됨' : 'Paid'}</span>
              <span className="tabular-nums" data-testid="bill-receipt-paid">
                {formatCurrency(receipt.amounts.paid, currency)}
              </span>
            </div>
          ) : null}
          {receipt.amounts.refunded > 0 ? (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{locale === 'es' ? 'Reembolsado' : locale === 'ko' ? '환불' : 'Refunded'}</span>
              <span>{formatCurrency(receipt.amounts.refunded, currency)}</span>
            </div>
          ) : null}
          <GuestPointsIncentive
            preview={receipt.membership_reward_preview}
            isLoggedIn={isLoggedIn}
            locale={locale}
            onJoin={() => {
              navigate(`${ROUTES.LOGIN}?returnTo=${encodeURIComponent(ROUTES.HOME)}`);
            }}
          />
        </div>
      ) : null}
    </section>
  );
}
